import { useEffect } from 'react';
import { Checkbox, Col, Form, FormInstance, Input, Row } from 'antd';
import { useIntl } from 'react-intl';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import CnpjFormField from '@/components/_common/CnpjFormField';
import { InputDecimal } from '@/components/_common';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';
import {
  useDefaultMinLengthFieldError,
  useDefaultMaxLengthFieldError,
  useDefaultEmptyFieldError,
  useDebounce,
  useDisclosure,
} from '@/hooks';
import { InsurerFormValues } from '@/core/domain/people/Insurers/Insurers.types';
import { validateCnpj } from '@/utils/functions/validateCnpj';
import { useImportedCpfCnpj } from '@/core/domain/people/ImportedCPFCNPJ/ImportedCPFCNPJ.hook';

interface Props {
  form: FormInstance<InsurerFormValues>;
  isEdit?: boolean;
}

const InsurerGeneralForm = ({ form, isEdit = false }: Props) => {
  const intl = useIntl();

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLenghtFieldError = useDefaultMinLengthFieldError();
  const [disable, setDisable] = useDisclosure(!isEdit);

  const { mutateAsync } = useImportedCpfCnpj();

  const cnpj = Form.useWatch('cnpj', form);
  const debounce = useDebounce(cnpj);

  const handleCpfCnpj = async (value: string | undefined) => {
    const mask = removeCpfCnpjMask(value);
    const isValidCnpj = validateCnpj(mask);

    if (isEdit) return;

    if (isValidCnpj) {
      setDisable.close();
      const data = await mutateAsync(mask);

      form.setFieldsValue({
        name: data.officialName,
        stateRegistration: data.stateRegistration,
        fantasyName: data.tradeName,
      });
    }
  };

  useEffect(() => {
    if (!isEdit) {
      const mask = removeCpfCnpjMask(cnpj);
      const isValidCnpj = validateCnpj(mask);
      if (!isValidCnpj) {
        setDisable.open();
        form.resetFields(['name', 'stateRegistration', 'fantasyName']);
      } else {
        setDisable.close();
      }
    }
  }, [cnpj]);

  useEffect(() => {
    handleCpfCnpj(debounce);
  }, [debounce]);

  return (
    <Form form={form} name="insurerGeneral">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <FloatingFormItem
            name="name"
            label={intl.formatMessage({ id: 'insurers.general.form.name' })}
            rules={[defaultEmptyFieldError]}
          >
            <Input disabled={disable} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12}>
          <FloatingFormItem
            name="fantasyName"
            label={intl.formatMessage({
              id: 'insurers.general.form.fantasyName',
            })}
          >
            <Input disabled={disable} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <CnpjFormField
            fieldName="cnpj"
            label={intl.formatMessage({
              id: 'insurers.general.form.cnpj',
            })}
            required
          />
        </Col>

        <Col xs={24} md={12} lg={8}>
          <FloatingFormItem
            name="stateRegistration"
            label={intl.formatMessage({
              id: 'insurers.general.form.stateRegistration',
            })}
            rules={[
              defaultMinLenghtFieldError(2),
              defaultMaxLenghtFieldError(15),
            ]}
          >
            <Input disabled={disable} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={24} lg={8}>
          <FloatingFormItem
            name="cityRegistration"
            label={intl.formatMessage({
              id: 'insurers.general.form.cityRegistration',
            })}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <FloatingFormItem
            name="email"
            label={intl.formatMessage({
              id: 'insurers.general.form.email',
            })}
            rules={[{ type: 'email' }]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} lg={8}>
          <FloatingFormItem
            name="refactoringMargin"
            label={intl.formatMessage({
              id: 'insurers.general.form.refactoringMargin',
            })}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={24} lg={8}>
          <FloatingFormItem
            name="website"
            label={intl.formatMessage({
              id: 'insurers.general.form.website',
            })}
          >
            <Input type="url" />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="observation"
            label={intl.formatMessage({
              id: 'insurers.general.form.observation',
            })}
          >
            <Input.TextArea rows={6} />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <Form.Item name="retentionIss" noStyle valuePropName="checked">
            <Checkbox>Haverá retenção de ISS para esta seguradora?</Checkbox>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default InsurerGeneralForm;
